import { useEffect, useState } from 'react';
import { api } from '../services/api';

interface ConditionalOrder {
  id: string;
  account: string;
  symbol: string;
  order_type: string;
  side: string;
  trigger_condition: string;
  trigger_price: number;
  limit_price: number | null;
  quantity: number;
  status: string;
  oco_pair_id: string | null;
  created_at: string;
  triggered_at: string | null;
  executed_at: string | null;
  executed_order_id: string | null;
  executed_price: number | null;
  executed_quantity: number | null;
  error_message: string | null;
  expires_at: string | null;
  notes: string | null;
}

interface ConditionalOrdersProps {
  refreshInterval?: number;
}

type OrderFormType = 'stop_loss' | 'take_profit' | 'oco';

export function ConditionalOrders({ refreshInterval = 10000 }: ConditionalOrdersProps) {
  const [orders, setOrders] = useState<ConditionalOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<OrderFormType>('stop_loss');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    account: 'ROKMM',
    side: 'sell' as 'buy' | 'sell',
    trigger_price: '',
    limit_price: '',
    quantity: '',
    // OCO specific
    stop_trigger_price: '',
    take_trigger_price: '',
    stop_limit_price: '',
    take_limit_price: '',
    expires_in_hours: '',
    notes: ''
  });

  useEffect(() => {
    loadOrders();
    loadCurrentPrice();
    const interval = setInterval(() => {
      loadOrders();
      loadCurrentPrice();
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadOrders = async () => {
    try {
      const response = await api.getConditionalOrders();
      setOrders(response.orders);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
      setLoading(false);
    }
  };

  const loadCurrentPrice = async () => {
    try {
      const data = await api.getSpreadStatus() as unknown as { bid?: number; ask?: number };
      if (data.bid && data.ask) {
        setCurrentPrice((data.bid + data.ask) / 2);
      }
    } catch {
      // Silently fail price update
    }
  };

  const cancelOrder = async (orderId: string) => {
    try {
      setCancelling(orderId);
      await api.cancelConditionalOrder(orderId);
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel order');
    } finally {
      setCancelling(null);
    }
  };

  const resetForm = () => {
    setFormData({
      account: 'ROKMM',
      side: 'sell',
      trigger_price: '',
      limit_price: '',
      quantity: '',
      stop_trigger_price: '',
      take_trigger_price: '',
      stop_limit_price: '',
      take_limit_price: '',
      expires_in_hours: '',
      notes: ''
    });
  };

  const submitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (formType === 'oco') {
        await api.createOCOOrder({
          account: formData.account,
          side: formData.side,
          stop_trigger_price: parseFloat(formData.stop_trigger_price),
          take_trigger_price: parseFloat(formData.take_trigger_price),
          quantity: parseFloat(formData.quantity),
          stop_limit_price: formData.stop_limit_price ? parseFloat(formData.stop_limit_price) : undefined,
          take_limit_price: formData.take_limit_price ? parseFloat(formData.take_limit_price) : undefined,
          expires_in_hours: formData.expires_in_hours ? parseInt(formData.expires_in_hours) : undefined,
          notes: formData.notes || undefined
        });
      } else if (formType === 'stop_loss') {
        await api.createStopLoss({
          account: formData.account,
          side: formData.side,
          trigger_price: parseFloat(formData.trigger_price),
          quantity: parseFloat(formData.quantity),
          limit_price: formData.limit_price ? parseFloat(formData.limit_price) : undefined,
          expires_in_hours: formData.expires_in_hours ? parseInt(formData.expires_in_hours) : undefined,
          notes: formData.notes || undefined
        });
      } else {
        await api.createTakeProfit({
          account: formData.account,
          side: formData.side,
          trigger_price: parseFloat(formData.trigger_price),
          quantity: parseFloat(formData.quantity),
          limit_price: formData.limit_price ? parseFloat(formData.limit_price) : undefined,
          expires_in_hours: formData.expires_in_hours ? parseInt(formData.expires_in_hours) : undefined,
          notes: formData.notes || undefined
        });
      }

      setShowForm(false);
      resetForm();
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price: number) => `$${price.toFixed(5)}`;
  const formatQty = (qty: number) => qty.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const formatTime = (iso: string) => new Date(iso).toLocaleString();

  const getOrderTypeLabel = (type: string) => {
    switch (type) {
      case 'stop_loss': return 'Stop Loss';
      case 'take_profit': return 'Take Profit';
      case 'oco_stop': return 'OCO Stop';
      case 'oco_take': return 'OCO Take';
      default: return type;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'triggered': return 'status-triggered';
      case 'executing': return 'status-executing';
      case 'executed': return 'status-executed';
      case 'cancelled': return 'status-cancelled';
      case 'expired': return 'status-expired';
      case 'failed': return 'status-failed';
      default: return '';
    }
  };

  const getTriggerDescription = (order: ConditionalOrder) => {
    const condition = order.trigger_condition === 'lte' ? '≤' : '≥';
    return `${condition} ${formatPrice(order.trigger_price)}`;
  };

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const historyOrders = orders.filter(o => o.status !== 'pending');

  if (loading && orders.length === 0) {
    return <div className="conditional-orders loading">Loading conditional orders...</div>;
  }

  return (
    <div className="conditional-orders">
      <div className="orders-header">
        <h3>Conditional Orders</h3>
        <div className="header-info">
          {currentPrice && <span className="current-price">FCO: {formatPrice(currentPrice)}</span>}
          <button onClick={() => setShowForm(!showForm)} className="new-order-btn">
            {showForm ? 'Cancel' : '+ New Order'}
          </button>
          <button onClick={loadOrders} className="refresh-btn" title="Refresh">↻</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="order-form-container">
          <div className="form-type-tabs">
            <button
              className={formType === 'stop_loss' ? 'active' : ''}
              onClick={() => setFormType('stop_loss')}
            >
              Stop Loss
            </button>
            <button
              className={formType === 'take_profit' ? 'active' : ''}
              onClick={() => setFormType('take_profit')}
            >
              Take Profit
            </button>
            <button
              className={formType === 'oco' ? 'active' : ''}
              onClick={() => setFormType('oco')}
            >
              OCO
            </button>
          </div>

          <form className="conditional-order-form" onSubmit={submitOrder}>
            <div className="form-grid">
              <div className="form-row">
                <label>Account</label>
                <select
                  value={formData.account}
                  onChange={(e) => setFormData({ ...formData, account: e.target.value })}
                >
                  <option value="ROKMM">ROKMM</option>
                  <option value="ROKVOL">ROKVOL</option>
                  <option value="TREASURY">TREASURY</option>
                </select>
              </div>

              <div className="form-row">
                <label>Side</label>
                <div className="side-buttons">
                  <button
                    type="button"
                    className={`side-btn sell ${formData.side === 'sell' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, side: 'sell' })}
                  >
                    Sell
                  </button>
                  <button
                    type="button"
                    className={`side-btn buy ${formData.side === 'buy' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, side: 'buy' })}
                  >
                    Buy
                  </button>
                </div>
              </div>

              <div className="form-row">
                <label>Quantity (FCO)</label>
                <input
                  type="number"
                  step="1"
                  placeholder="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                />
              </div>

              {formType === 'oco' ? (
                <>
                  <div className="form-row">
                    <label>Stop Trigger Price</label>
                    <input
                      type="number"
                      step="0.00001"
                      placeholder={currentPrice ? (currentPrice * 0.95).toFixed(5) : '0.00000'}
                      value={formData.stop_trigger_price}
                      onChange={(e) => setFormData({ ...formData, stop_trigger_price: e.target.value })}
                      required
                    />
                    <span className="hint">Triggers when price drops to this level</span>
                  </div>

                  <div className="form-row">
                    <label>Take Profit Trigger</label>
                    <input
                      type="number"
                      step="0.00001"
                      placeholder={currentPrice ? (currentPrice * 1.05).toFixed(5) : '0.00000'}
                      value={formData.take_trigger_price}
                      onChange={(e) => setFormData({ ...formData, take_trigger_price: e.target.value })}
                      required
                    />
                    <span className="hint">Triggers when price rises to this level</span>
                  </div>

                  <div className="form-row">
                    <label>Stop Limit Price (optional)</label>
                    <input
                      type="number"
                      step="0.00001"
                      placeholder="Market order if empty"
                      value={formData.stop_limit_price}
                      onChange={(e) => setFormData({ ...formData, stop_limit_price: e.target.value })}
                    />
                  </div>

                  <div className="form-row">
                    <label>Take Limit Price (optional)</label>
                    <input
                      type="number"
                      step="0.00001"
                      placeholder="Market order if empty"
                      value={formData.take_limit_price}
                      onChange={(e) => setFormData({ ...formData, take_limit_price: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <label>Trigger Price</label>
                    <input
                      type="number"
                      step="0.00001"
                      placeholder="0.00000"
                      value={formData.trigger_price}
                      onChange={(e) => setFormData({ ...formData, trigger_price: e.target.value })}
                      required
                    />
                    <span className="hint">
                      {formType === 'stop_loss'
                        ? `${formData.side === 'sell' ? 'Triggers when price ≤' : 'Triggers when price ≥'} this value`
                        : `${formData.side === 'sell' ? 'Triggers when price ≥' : 'Triggers when price ≤'} this value`
                      }
                    </span>
                  </div>

                  <div className="form-row">
                    <label>Limit Price (optional)</label>
                    <input
                      type="number"
                      step="0.00001"
                      placeholder="Market order if empty"
                      value={formData.limit_price}
                      onChange={(e) => setFormData({ ...formData, limit_price: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="form-row">
                <label>Expires In (hours)</label>
                <input
                  type="number"
                  step="1"
                  placeholder="Never expires"
                  value={formData.expires_in_hours}
                  onChange={(e) => setFormData({ ...formData, expires_in_hours: e.target.value })}
                />
              </div>

              <div className="form-row full-width">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  placeholder="Optional note"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Creating...' : `Create ${getOrderTypeLabel(formType)}`}
            </button>
          </form>
        </div>
      )}

      <div className="orders-section">
        <h4>Pending Orders ({pendingOrders.length})</h4>
        {pendingOrders.length === 0 ? (
          <div className="no-orders">No pending conditional orders</div>
        ) : (
          <div className="orders-table">
            <div className="table-header">
              <span>Type</span>
              <span>Account</span>
              <span>Side</span>
              <span>Trigger</span>
              <span>Limit</span>
              <span>Qty</span>
              <span>Created</span>
              <span>Action</span>
            </div>
            {pendingOrders.map((order) => (
              <div key={order.id} className={`order-row ${order.side} ${order.order_type}`}>
                <span className="order-type">
                  {getOrderTypeLabel(order.order_type)}
                  {order.oco_pair_id && <span className="oco-badge">OCO</span>}
                </span>
                <span className="account">{order.account}</span>
                <span className={`side ${order.side}`}>{order.side.toUpperCase()}</span>
                <span className="trigger">{getTriggerDescription(order)}</span>
                <span className="limit">{order.limit_price ? formatPrice(order.limit_price) : 'Market'}</span>
                <span className="quantity">{formatQty(order.quantity)}</span>
                <span className="created">{new Date(order.created_at).toLocaleTimeString()}</span>
                <span className="actions">
                  <button
                    className="cancel-btn"
                    onClick={() => cancelOrder(order.id)}
                    disabled={cancelling === order.id}
                  >
                    {cancelling === order.id ? '...' : 'Cancel'}
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="orders-section history">
        <h4>Order History ({historyOrders.length})</h4>
        {historyOrders.length === 0 ? (
          <div className="no-orders">No order history</div>
        ) : (
          <div className="orders-table">
            <div className="table-header">
              <span>Type</span>
              <span>Account</span>
              <span>Side</span>
              <span>Trigger</span>
              <span>Executed</span>
              <span>Status</span>
              <span>Time</span>
            </div>
            {historyOrders.slice(0, 20).map((order) => (
              <div key={order.id} className={`order-row ${order.side} ${order.status}`}>
                <span className="order-type">{getOrderTypeLabel(order.order_type)}</span>
                <span className="account">{order.account}</span>
                <span className={`side ${order.side}`}>{order.side.toUpperCase()}</span>
                <span className="trigger">{getTriggerDescription(order)}</span>
                <span className="executed">
                  {order.executed_price ? (
                    <>{formatPrice(order.executed_price)} x {formatQty(order.executed_quantity || 0)}</>
                  ) : '-'}
                </span>
                <span className={`status ${getStatusClass(order.status)}`}>
                  {order.status}
                  {order.error_message && (
                    <span className="error-tooltip" title={order.error_message}>!</span>
                  )}
                </span>
                <span className="time">
                  {order.executed_at
                    ? formatTime(order.executed_at)
                    : order.triggered_at
                      ? formatTime(order.triggered_at)
                      : formatTime(order.created_at)
                  }
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
